import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { useTranslation } from 'react-i18next';
import '@/components/utils/i18n';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Shield, 
  Search, 
  FileSearch, 
  Users, 
  TrendingUp, 
  Lock,
  CheckCircle2,
  AlertTriangle,
  Globe,
  Database,
  Zap,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import BrowserMockup from '@/components/landing/BrowserMockup';
import DashboardMockup from '@/components/landing/DashboardMockup';

export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Don't auto-redirect authenticated users - let them view pricing
  // They can navigate to dashboard via header

  const handleGetStarted = (packageType = '') => {
    // Store package selection before redirecting to sign up
    if (packageType) {
      localStorage.setItem('selectedPackage', packageType);
    }
    // Redirect to Base44 sign up, will come back to onboarding after authentication
    base44.auth.redirectToLogin(createPageUrl('Onboarding'));
  };

  const features = [
    {
      icon: Search,
      title: t('landing.comprehensiveScreening'),
      description: t('landing.comprehensiveScreeningDesc')
    },
    {
      icon: Database,
      title: t('landing.multipleDataSources'),
      description: t('landing.multipleDataSourcesDesc')
    },
    {
      icon: Zap,
      title: t('landing.aiAnalysis'),
      description: t('landing.aiAnalysisDesc')
    },
    {
      icon: AlertTriangle,
      title: t('landing.anomalyDetection'),
      description: t('landing.anomalyDetectionDesc')
    },
    {
      icon: FileSearch,
      title: t('landing.detailedReporting'),
      description: t('landing.detailedReportingDesc')
    },
    {
      icon: Users,
      title: t('landing.teamCollaboration'),
      description: t('landing.teamCollaborationDesc')
    }
  ];

  const stats = [
    { value: t('landing.global'), label: t('landing.coverage') },
    { value: t('landing.24x7'), label: t('landing.monitoring') },
    { value: t('landing.realtime'), label: t('landing.updates') },
    { value: t('landing.secure'), label: t('landing.platform') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30" style={{ scrollBehavior: 'smooth' }}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-md border-b border-slate-200' : 'bg-transparent border-b border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6960b95861a068b7e8045644/963ec1955_WC360noBgColor.png"
                alt="World Check 360"
                className="h-8 sm:h-10 w-auto"
              />
            </div>

            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                {t('landing.features')}
              </a>
              <a href="#screening" className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                {t('landing.screening')}
              </a>
              <a href="#pricing" className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                {t('landing.pricing')}
              </a>
              <Link to={createPageUrl('ContactSales')} className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                Contact Us
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <LanguageSwitcher className={scrolled ? 'text-slate-700' : 'text-white hover:bg-white/10 hover:text-white'} />
              <Button
                onClick={() => base44.auth.redirectToLogin(createPageUrl('Dashboard'), { mode: 'signin' })}
                variant="outline"
                className={scrolled ? 'bg-white border-rose-200 text-rose-600 hover:bg-rose-50' : 'bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white'}
              >
                {t('landing.signIn')}
              </Button>
              <Button
                onClick={() => base44.auth.redirectToLogin(createPageUrl('Onboarding'), { mode: 'signup' })}
                className="bg-rose-500 hover:bg-rose-600"
              >
                Sign Up
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-slate-950">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          }}
        />
        {/* Glowing orbs */}
        <div className="absolute top-[-10%] left-[15%] w-[500px] h-[500px] bg-rose-500/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] bg-indigo-500/15 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[10%] left-[40%] w-[350px] h-[350px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 backdrop-blur-md rounded-full text-rose-300 text-sm font-medium mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                </span>
                <Shield className="w-4 h-4" />
                {t('landing.enterpriseCompliance')}
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                <span className="bg-gradient-to-r from-white via-white to-rose-200 bg-clip-text text-transparent">
                  {t('landing.mainTitle')}
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl text-slate-400 mb-10 leading-relaxed max-w-3xl mx-auto">
                {t('landing.mainDescription')}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  onClick={() => handleGetStarted('starter')}
                  size="lg"
                  className="bg-rose-500 hover:bg-rose-600 shadow-[0_0_30px_rgba(244,63,94,0.4)] hover:shadow-[0_0_40px_rgba(244,63,94,0.6)] text-lg px-8 h-14 transition-all"
                >
                  Start Free - No Credit Card
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  variant="outline"
                  className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white text-lg px-8 h-14 backdrop-blur-md"
                >
                  Explore Features
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap text-xs sm:text-sm text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  SOC 2 Type II
                </div>
                <div className="w-px h-3 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  GDPR Ready
                </div>
                <div className="w-px h-3 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  ISO 27001
                </div>
                <div className="w-px h-3 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  No Credit Card Required
                </div>
              </div>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-5 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-md">
                <div className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-rose-300 bg-clip-text text-transparent mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* Dashboard Preview */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 via-purple-500/20 to-indigo-500/20 rounded-3xl blur-2xl opacity-60" />
              <div className="relative">
                <DashboardMockup delay={0.4} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.complianceSolutions')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.complianceSolutionsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm h-full hover:shadow-2xl transition-shadow">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-rose-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-rose-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Detailed Risk Analysis at Your Fingertips
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Every screening delivers a comprehensive risk profile with match-by-match
                breakdowns, confidence scores, and source attribution — so your team can
                make informed decisions fast.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">Match confidence scoring</span>
                    <p className="text-slate-600 text-sm">AI-powered confidence scores from 0-100% for every potential match.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">Multi-source attribution</span>
                    <p className="text-slate-600 text-sm">Every match links back to its original source — OFAC, UN, EU, UK, and more.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">Analyst review workflow</span>
                    <p className="text-slate-600 text-sm">Mark false positives, add notes, and assign follow-up actions directly from the results page.</p>
                  </div>
                </li>
              </ul>
            </div>
            <BrowserMockup
              src="https://media.base44.com/images/public/6960b95861a068b7e8045644/e81427206_generated_image.png"
              alt="Screening Results Detail"
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* PDF Report Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <BrowserMockup
              src="https://media.base44.com/images/public/6960b95861a068b7e8045644/c8f19fd0a_generated_image.png"
              alt="PDF Compliance Report"
              delay={0.2}
            />
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Professional Reports in One Click
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Export audit-ready compliance reports as PDF or JSON with a single click —
                complete with risk ratings, source attribution, and barcode-tracked reference IDs.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">PDF & JSON export</span>
                    <p className="text-slate-600 text-sm">Download formatted reports for regulators, auditors, or your internal records.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">Full source transparency</span>
                    <p className="text-slate-600 text-sm">Every data source checked is listed — OFAC, UN, EU, UK, PEP, SOE, UBO and more.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">Barcode-tracked reference IDs</span>
                    <p className="text-slate-600 text-sm">Each report carries a scannable reference ID for easy retrieval and chain-of-custody.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Screening Categories */}
      <section id="screening" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.whatWeScreen')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive screening across multiple categories and global data sources. Optional screening against UANI, IEI, and GUR for enhanced intelligence (does not affect risk scoring).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: t('landing.pep'),
                description: t('landing.pepDesc'),
                icon: Shield
              },
              {
                title: t('landing.sanctions'),
                description: t('landing.sanctionsDesc'),
                icon: AlertTriangle
              },
              {
                title: t('landing.adverseMediaTitle'),
                description: t('landing.adverseMediaDesc'),
                icon: FileSearch
              },
              {
                title: t('landing.regulatory'),
                description: t('landing.regulatoryDesc'),
                icon: Lock
              },
              {
                title: t('landing.soeTitle'),
                description: t('landing.soeDesc'),
                icon: Globe
              },
              {
                title: t('landing.uboTitle'),
                description: t('landing.uboDesc'),
                icon: Users
              },
              {
                title: 'UANI (United Against Nuclear Iran)',
                description: 'Screen against entities involved in nuclear proliferation activities and Iran-related sanctions violations',
                icon: Shield
              },
              {
                title: 'IEI (Iran Entities & Individuals)',
                description: 'Check against comprehensive database of Iranian entities and individuals subject to international restrictions',
                icon: Database
              },
              {
                title: 'GUR (Ukrainian Intelligence)',
                description: 'Access Ukrainian military intelligence sanctions data for war-related compliance screening',
                icon: AlertTriangle
              }
            ].map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-rose-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                          {category.title}
                        </h3>
                        <p className="text-slate-600 text-sm">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.simplePricing')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.pricingDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Starter Plan */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-700 mb-2">{t('landing.starter')}</h3>
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-blue-700">FREE</span>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">{t('landing.starterDesc')}</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">5 screenings per day</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.basicCompliance')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.standardDataSources')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.basicReporting')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.emailSupport')}</span>
                  </li>
                </ul>
                <Button 
                  onClick={handleGetStarted}
                  className="w-full bg-rose-500 hover:bg-rose-600"
                >
                  Free Signup
                </Button>
              </CardContent>
            </Card>

            {/* Professional Plan */}
            <Card className="border-2 border-rose-500 shadow-2xl bg-gradient-to-br from-rose-50 to-white relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  {t('landing.mostPopular')}
                </span>
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-rose-700 mb-2">{t('landing.professional')}</h3>
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-rose-700">$49</span>
                    <span className="text-slate-400 text-2xl line-through">$150</span>
                    <span className="text-slate-600">/month</span>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">{t('landing.professionalDesc')}</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">500 {t('landing.screeningsPerMonth')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.allDataSources')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.advancedAI')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.bulkScreening')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.anomalyDetection')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.prioritySupport')}</span>
                  </li>
                </ul>
                <Button 
                  onClick={() => handleGetStarted('professional')}
                  className="w-full bg-rose-500 hover:bg-rose-600"
                >
                  Buy Now
                </Button>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-purple-700 mb-2">{t('landing.enterprise')}</h3>
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-purple-700">$149</span>
                    <span className="text-slate-400 text-2xl line-through">$299</span>
                    <span className="text-slate-600">/month</span>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">{t('landing.enterpriseDesc')}</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.unlimitedScreenings')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.unlimitedUsers')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">UANI, IEI, GUR screening options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.dedicatedManager')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{t('landing.premiumSupport')}</span>
                  </li>
                </ul>
                <Button 
                  onClick={() => handleGetStarted('enterprise')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Buy Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-700 to-purple-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.readyToStreamline')}
          </h2>
          <p className="text-xl text-slate-200 mb-8">
            {t('landing.joinOrganizations')}
          </p>
          <Button 
            onClick={() => handleGetStarted('starter')}
            size="lg"
            className="bg-white text-slate-700 hover:bg-slate-50 shadow-xl text-lg px-8"
          >
            Start Free - No Credit Card
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-4">
            <img 
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6960b95861a068b7e8045644/963ec1955_WC360noBgColor.png" 
              alt="World Check 360" 
              className="h-8 w-auto mx-auto opacity-75"
            />
          </div>
          <p className="text-sm">
            {t('landing.copyright', { year: new Date().getFullYear() })}
          </p>
          <p className="text-xs mt-2">
            {t('landing.poweredBy')}
          </p>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <Link to="/About" className="hover:text-white">About</Link>
            <Link to="/Contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}